/**
 * Tiny localStorage helpers for the shell (E10-F01-T03).
 *
 * Two persisted slices, both parity with the legacy `core.js`:
 *   - `route.save(route)` — last visited route, restored on a cold load when the
 *     URL is the bare origin (so a refresh keeps you where you were);
 *   - the sidebar collapsed flag (`eer.sidebarCollapsed`, the exact legacy key,
 *     stored as `'1'` when collapsed and `''` (empty string) otherwise — the
 *     same `'1' | ''` truthiness the legacy `toggleSidebar`/`restoreSidebar`
 *     pair used).
 *
 * Every access is wrapped: `localStorage` can throw (private mode, disabled
 * storage, SSR/tests with no DOM) and a UI-chrome preference must never crash
 * the app. On any failure we silently fall back to the in-memory default.
 */
import { pathToRoute, routeToPath, type Route } from './routes.js';

const ROUTE_KEY = 'eer.route';
const SIDEBAR_KEY = 'eer.sidebarCollapsed';

function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* storage unavailable — preference is best-effort only */
  }
}

/** Persist the last route as a path (`route.save` in the legacy core). */
export function saveRoute(route: Route): void {
  safeSet(ROUTE_KEY, routeToPath(route));
}

/** Read the persisted route, or `null` if none/unreadable. */
export function loadRoute(): Route | null {
  const raw = safeGet(ROUTE_KEY);
  return raw ? pathToRoute(raw) : null;
}

/**
 * Persist the sidebar collapsed flag using the legacy `'1' | ''` convention:
 * `'1'` when collapsed, an empty string when expanded (parity with the legacy
 * `toggleSidebar`, which wrote `localStorage.setItem('eer.sidebarCollapsed',
 * collapsed ? '1' : '')`).
 */
export function saveSidebarCollapsed(collapsed: boolean): void {
  safeSet(SIDEBAR_KEY, collapsed ? '1' : '');
}

/**
 * Read the sidebar collapsed flag (defaults to expanded). Mirrors the legacy
 * `restoreSidebar`, which collapsed the shell only when the stored value was
 * exactly `'1'`; any other value (including the empty string, `'0'`, or a
 * missing key) restores the expanded state.
 */
export function loadSidebarCollapsed(): boolean {
  return safeGet(SIDEBAR_KEY) === '1';
}
